// Modal for uploading a JSON file to repopulate the database

import React, { useState } from 'react';
import { Modal, ModalHeader, ModalDescription, ModalContent, ModalActions, Button, Header, Segment, Icon, Message, Dimmer, Loader } from 'semantic-ui-react';
import ConfirmationModal from './ConfirmationModal';
import Ajv from 'ajv';
import axios from "axios";


const UploadModal = ({ open, onClose, setRepopulated }) => {
    const hostingServer = `https://sugardb.cs.uct.ac.za/`;

    const [confirmationOpen, setConfirmationOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const [file, setFile] = useState(null);
    const [fileContent, setFileContent] = useState(null);
    const [validationError, setValidationError] = useState(null);

    // Use AJV to validate the schema of the JSON file (for organism)
    const validateSchema = (data) => {
        const ajv = new Ajv();
        const schema = {
            type: "array",
            items: {
                type: "object",
                properties: {
                    Serotype: { type: "string" },
                    Antigen_structure: { type: "string" },
                    Carbuilder_structure_format: { type: "string" },
                    NMR: { type: "string" },
                    Species_where_found: { type: "string" },
                    Nickname: { type: "string" },
                    Antigen_type: { type: "string" },
                    Molecular_type: { type: "string" },
                    DOI: { type: "string" },
                    References: { type: "string" },
                    Branched: { type: "number" },
                    Repeat_unit_size: { type: "number" },
                },
                required: ["Serotype", "Antigen_structure", "Carbuilder_structure_format", "Antigen_type", "DOI"]
            }
        };

        const validate = ajv.compile(schema);
        const valid = validate(data);

        if (!valid) {
            console.error('Validation errors: ', validate.errors);
            setValidationError('Invalid file format. Please follow the format provided in the description.');
            return false;
        } else {
            return true;
        }
    };

    // read the file and set the content if schema is valid
    const handleFileUpload = (e) => {
        console.log('Uploading file:', e.target.files[0]);
        const uploadedFile = e.target.files[0];
        setFile(uploadedFile);

        const reader = new FileReader();
        reader.onload = (e) => {
            const content = e.target.result;
            
            try {
                const data = JSON.parse(content);
                if (validateSchema(data)) {
                    setFileContent(content);
                    setValidationError(null);
                } else {
                    setFileContent(null);
                }
            } catch (err) {
                console.error("Error parsing JSON:", err);
                setValidationError('Invalid JSON file. Please ensure the file is properly formatted.');
                setFileContent(null);
            }
        };
        reader.readAsText(uploadedFile);
    };

    // Send the file content to the server to repopulate the database
    const repopulateDatabase = async (data) => {
        console.log('File content:', data);
        try {
            const response = await axios.post(`${hostingServer}api/repopulate`, data);
            console.log('Repopulate response:', response.data);
            setLoading(false);
            setConfirmationOpen(false);
            setRepopulated(true);
            onClose();
        } catch (error) {
            console.error('Error repopulating database:', error);
            setLoading(false);
            setConfirmationOpen(false);

            // alert for 400 error
            if (error.response && error.response.status === 400) {
                alert('Invalid data format. Please ensure the file is properly formatted.');
            } else {
                alert('Error repopulating database. Please try again.');
            }
        }
    };

    // Confirm the repopulation of the database
    const handleConfirm = () => {
        console.log('Repopulating database');
        setLoading(true);

        if (!fileContent) {
            console.error('No file content to send');
            setLoading(false);
            return;
        }

        const data = JSON.parse(fileContent);
        if (!validateSchema(data)) {
            console.error('Invalid file format');
            setLoading(false);
            return;
        }

        // Send the file to the server
        repopulateDatabase(data);
    };

    // Open the confirmation modal
    const openConfirmationModal = () => {
        setConfirmationOpen(true);
    };

    return (
        <> 
        <Modal closeIcon open={open} onClose={onClose}>
            <ModalHeader>Repopulate the database.</ModalHeader>
            <ModalContent>
                <ModalDescription> 
                    <p>Please note the following example for the format of the JSON file:</p> 
                    <p>
                        {'[{'}
                        <br />
                        {'    "Serotype": "K1",'}
                        <br />
                        {'    "Antigen_structure": "→4)-[2,3-Pyr]-β-D-GlcpA-(1→4)-α-L-Fucp-(1→3)-β-D-Glcp-(1→",'}
                        <br />
                        {'    "Carbuilder_structure_format": "->4)[2,3-Pyr]bDGlcA(1->4)aLFuc(1->3)bDGlc(1->",'}
                        <br />
                        {'    "NMR": "5.28; 4.95; 4.64; 1.55 (Pyr); 1.23 (CH3)",'}
                        <br />
                        {'    "Species_where_found": "Klebsiella pneumoniae",'}
                        <br />
                        {'    "Nickname": "Kp",'}
                        <br />
                        {'    "Antigen_type": "K-antigen",'}
                        <br />
                        {'    "Molecular_type": "Molecular type",'}
                        <br />
                        {'    "DOI": "https://doi.org/10.1016/S0008-6215(00)84088-4",'}
                        <br />
                        {'    "References": "Christina Erbing, Lennart Kenne, Bengt Lindberg, Jörgen Lönngren, Ian W. Sutherland,\\nStructural studies of the capsular polysaccharide from Klebsiella type 1, Carbohydrate Research, Volume 50, Issue 1, 1976, 115-120.",'}
                        <br />
                        {'    "Branched": 0,'}
                        <br />
                        {'    "Repeat_unit_size": 3'}
                        <br />
                        {'}, '}
                        <br />
                        {'... more entries here ...'}
                        <br />
                        {']}'}
                    </p>
                </ModalDescription>
                <Segment placeholder>
                    <Header icon>
                        <Icon name='file outline' />
                        {file ? file.name : 'No JSON file uploaded'}
                    </Header>
                    
                    <Button primary as="label">
                        Upload JSON file
                        <input type="file" hidden accept=".json,application/json" onChange={handleFileUpload} />
                    </Button>

                    {validationError && <Message negative>{validationError}</Message>}
                </Segment>
            </ModalContent>

            <ModalActions>
                <Button disabled={validationError || !file } positive icon='upload' content='Populate database' onClick={() => openConfirmationModal()}/>
            </ModalActions>

            {loading && (
                <Dimmer active size='small'>
                    <Loader indeterminate>Repopulating database</Loader>
                </Dimmer>
            )};
        </Modal>

        <ConfirmationModal
            open={confirmationOpen}
            onClose={() => setConfirmationOpen(false)}
            onConfirm={handleConfirm}
            message={`Are you sure you want to repopulate the database?`}
        />
        </>
    );
}

export default UploadModal;
